<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { QuizType } from '@/enums/quiz.enums';
import { KickoffMasteryTrackingEvents } from "~/enums/tracking/kickoff-mastery.enums";
import { CourseType } from "@/enums/course.enums";

const route = useRoute();

const { getNextActivity, getActivityUrl, refreshCourse, isLoading: isLoadingCourse, course } = useCourseState();
const { dispatchTrackingEvent } = useTracking();
const { quiz, currentAttempt, restartQuiz, isLoading } = useQuizStore();

const quotes = {
  pass: [
    {
      author: 'Robert Collier',
      quote: 'Success is the sum of small efforts, repeated day in and day out.',
    },
    {
      author: 'Booker T. Washington',
      quote: 'You are not judged by the height you have reached, but by the obstacles you have overcome.',
    },
    {
      author: 'Albert Schweitzer',
      quote: 'Success is not the key to happiness. Happiness is the key to success. If you love what you are doing, you will be successful.',
    },
    {
      author: 'Roy T. Bennett',
      quote: 'Success is not how high you have climbed, but how you make a positive difference to the world.',
    },
    {
      author: 'Aisha Tyler',
      quote: 'Success is not the absence of failure; it\'s the persistence through failure.',
    },
    {
      author: 'G.K. Nielson',
      quote: 'Successful people are not gifted; they just work hard, then succeed on purpose.',
    },
  ],
  fail: [
    {
      author: 'Franklin D. Roosevelt',
      quote: 'The only limit to our realization of tomorrow will be our doubts of today.',
    },
    {
      author: 'Winston Churchill',
      quote: 'Success is not final, failure is not fatal: It is the courage to continue that counts.',
    },
    {
      author: 'Joel Brown',
      quote: 'The only thing that stands between you and your dream is the will to try and the belief that it is actually possible.',
    },
    {
      author: 'Zig Ziglar',
      quote: 'You don\'t have to be great to start, but you have to start to be great.',
    },
    {
      author: 'Unknown',
      quote: 'Success is not about being the best. It\'s about always getting better.',
    },
    {
      author: 'Nelson Mandela',
      quote: 'The greatest glory in living lies not in never falling, but in rising every time we fall.',
    },
    {
      author: 'Anonymous',
      quote: 'Success is not a destination, it\'s a journey. It\'s about becoming the best version of yourself.',
    },
    {
      author: 'Herman Cain',
      quote: 'Success is not the key to happiness. Happiness is the key to success. If you love what you are doing, you will be successful.',
    },
  ],
};

const isPassed = computed(() => {
  return currentAttempt.value.isPassed;
});

const isKickoffQuiz = computed(() => quiz.value?.type === QuizType.Kickoff);
const isMasteryQuiz = computed(() => quiz.value?.type === QuizType.Mastery);

const quote = computed(() => {
  return isPassed.value
    ? quotes.pass[Math.floor(Math.random() * quotes.pass.length)]
    : quotes.fail[Math.floor(Math.random() * quotes.fail.length)];
});

const nextActivity = computed(() => {
  return getNextActivity(route.params.quizId as string);
});

const nextActivityUrl = computed(() => {
  const next = nextActivity.value;

  if (!next) {
    return `/courses/${route.params.courseId}`;
  }

  return getActivityUrl(next);
});

const handleKickoffStartLearningButton = async () => {
  dispatchTrackingEvent(KickoffMasteryTrackingEvents.LearningStarted);

  await next();
};

const next = async () => {
  await refreshCourse();

  navigateTo(nextActivityUrl.value);
};

const retakeQuiz = async () => {
  await restartQuiz();
};

const isMaxScore = computed(() => currentAttempt.value?.score === 100);
</script>

<template>
  <div class="h-full flex justify-center items-center gap-20 sm:px-8 px-4">
    <div class="sm:w-[343px] w-full">
      <Card
        class="shadow-medmastery rounded-2xl"
        :pt="{
          content: {
            class: 'p-0'
          },
          body: {
            class: 'px-6 pt-8 pb-6'
          }
        }"
      >
        <template
          v-if="isKickoffQuiz"
          #content
        >
          <div class="text-center text-22 text-neutral-90 font-bold mb-1">
            {{isMaxScore ? 'Good Job!' : 'No worries!'}}
          </div>

          <div class="text-neutral-80 text-16 text-center">
            {{isMaxScore ? 'This is a great starting point!' : 'You’ve made a good start!'}}
          </div>

          <div class="flex justify-center my-6">
            <img
              v-if="isMaxScore"
              src="@/assets/images/gifs/cup.gif"
              class="w-[150px] aspect-square"
              alt="Cup animation"
            >
            <img
              v-else
              src="@/assets/images/gifs/puzzle.gif"
              class="w-[150px] aspect-square"
              alt="Puzzle animation"
            >
          </div>

          <div class="text-center uppercase text-neutral-70 text-16 font-bold">
            Your score
          </div>

          <div
            v-if="quiz?.questions?.length"
            class="text-48 font-bold text-center text-neutral-100 mt-1"
          >
            {{Math.round(currentAttempt.score * quiz?.questions?.length / 100)}} / {{quiz?.questions?.length}}
          </div>

          <div class="text-center text-14 text-neutral-80 my-6">
            <template v-if="isMaxScore">
              Now that you know where you stand, let’s dive into the course material.
            </template>

            <template v-else>
              Now, let’s fill in the missing pieces and turn your knowledge into mastery!
            </template>
          </div>

          <div class="flex justify-center">
            <Button
              v-if="nextActivityUrl"
              class="font-bold text-14 flex gap-2"
              @click="handleKickoffStartLearningButton()"
            >
              <span>Start learning</span>
              <FontAwesomeIcon icon="fa-forward-step" />
            </Button>
          </div>
        </template>

        <template
          v-else
          #content
        >
          <div class="flex flex-col gap-6">
            <div>
              <div
                :class="[
                  'text-center text-22 font-bold mb-1',
                  isPassed ? 'text-green-80' : 'text-red-40'
                ]"
              >
                <span class="capitalize">{{course?.type === CourseType.Workshop ? 'case' : 'quiz'}}</span> {{isPassed ? 'passed' : 'not passed'}}
              </div>

              <div class="text-center text-16 text-neutral-80">
                <template v-if="isPassed">
                  Congratulations, keep it up
                </template>

                <template v-else>
                  You need to reach {{(quiz?.passRate || 70) * 100}}% to pass this {{course?.type === CourseType.Workshop ? 'case' : 'quiz'}}
                </template>
              </div>
            </div>

            <div class="flex justify-center">
              <div class="w-[187px] h-[187px] flex">
                <img
                  v-if="isPassed"
                  src="@/assets/images/gifs/confetti.gif"
                  alt="Checkmark circle"
                >

                <img
                  v-else
                  src="@/assets/images/gifs/cross-circle.gif"
                  alt="Restart gif"
                >
              </div>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <Card
                v-if="quiz?.questions?.length"
                class="shadow-medmastery quiz-result-stat-card"
                :pt="{
                  body: {
                    class: 'p-0'
                  }
                }"
              >
                <template #content>
                  <div class="grid grid-rows-2 items-end">
                    <div class="text-center text-neutral-80 text-36 font-bold">
                      {{Math.round(currentAttempt.score * quiz?.questions?.length / 100)}}/{{quiz?.questions?.length}}
                    </div>
                    <div class="text-center text-neutral-60 text-14 font-bold uppercase leading-normal">
                      Correct<br> answers
                    </div>
                  </div>
                </template>
              </Card>

              <Card
                class="shadow-medmastery quiz-result-stat-card"
                :pt="{
                  body: {
                    class: 'p-0'
                  }
                }"
              >
                <template #content>
                  <div class="grid grid-rows-2 items-end">
                    <div
                      class="text-center text-36 font-bold"
                      :class="isPassed ? 'text-neutral-80' : 'text-red-40'"
                    >
                      {{currentAttempt.score}}%
                    </div>
                    <div class="text-center text-neutral-60 text-14 font-bold uppercase leading-normal">
                      Score
                    </div>
                  </div>
                </template>
              </Card>
            </div>

            <div class="flex items-center gap-3">
              <Button
                v-if="!isKickoffQuiz && !isMasteryQuiz"
                :severity="isPassed ? 'secondary' : 'primary'"
                :outlined="isPassed"
                :loading="isLoading"
                :disabled="isLoading || isLoadingCourse"
                class="font-bold text-16 rounded-full py-3 px-4.5"
                :class="isPassed ? 'text-neutral-80 border-2' : 'w-full bg-blue-30 border-blue-30 text-neutral-90'"
                label="Retake"
                @click="retakeQuiz"
              />

              <Button
                :severity="isPassed ? 'primary' : 'secondary'"
                :outlined="!isPassed"
                :disabled="isLoading || isLoadingCourse"
                :loading="isLoading || isLoadingCourse"
                class="font-bold text-16 rounded-full py-3 px-4.5"
                :class="isPassed ? 'w-full bg-blue-30 border-blue-30 text-neutral-90' : 'text-neutral-80 border-2'"
                label="Next"
                @click="next()"
              />
            </div>
          </div>
        </template>
      </Card>

      <div
        v-if="!isKickoffQuiz && !isMasteryQuiz"
        class="text-neutral-80 text-center text-14 mt-4"
      >
        You can retake the {{course?.type === CourseType.Workshop ? 'case' : 'quiz'}} as many times as you like to improve your score and master this material.
      </div>
    </div>

    <div class="xl:block hidden min-w-[290px] max-w-[290px]">
      <div class="text-20 font-bold relative text-neutral-60 z-[2]">
        <span
          class="absolute inline-block text-neutral-10 -z-[1] text-28"
          style="transform: scale(5) translateY(0.2em);"
        >
          &ldquo;
        </span>
        {{quote.quote}}
        <span>&rdquo;</span>
      </div>

      <div class="text-18 text-right mt-2 text-neutral-70">
        &mdash; {{quote.author}}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.quiz-result-stat-card {
  border: 1px solid var(--palette-neutral-30);
}
</style>